// The Dashboard's figures that are worked out in the browser from what the
// server answered (D-469): the runs of each pipeline card's bar, and the
// in-and-out chart's columns per payment cycle. Plain functions, so the
// components that draw them export components only and the tests can call
// them.

export interface StepFigures {
  count: number
  total: number
  medianDays: number | null
  /** Invoices per uploaded-age band, in band order. Null when the split did not load. */
  ages: number[] | null
  /** Invoices here uploaded more than the past-due threshold ago, when the caller read them. */
  pastDue?: number
}

/** One run of a step's bar: an age band by index, or `null` for invoices with no band to show. */
interface BarSegment {
  band: number | null
  count: number
}

/**
 * The runs a step card's bar draws, youngest band first (D-469). Together they
 * are the step's own count, so the bar is that step's 100%: the age bands with
 * anything in them, then a neutral run for whatever the band split did not
 * account for (or the whole count, when the split did not load). A step with
 * nothing in it has no runs, and draws the bare track.
 */
export function pipelineBarSegments(figures: StepFigures): BarSegment[] {
  if (figures.count === 0) return []
  if (figures.ages === null) return [{ band: null, count: figures.count }]
  const segments: BarSegment[] = figures.ages.flatMap((count, band) => (count > 0 ? [{ band, count }] : []))
  const counted = figures.ages.reduce((sum, n) => sum + n, 0)
  if (figures.count > counted) segments.push({ band: null, count: figures.count - counted })
  return segments
}

export interface InOutDay {
  /** `YYYY-MM-DD`, Eastern: the day, or the Wednesday a payment cycle starts. */
  date: string
  /** The column's tooltip name, when it differs from `label`. */
  title?: string
  label: string
  in: number
  out: number
}

const DAY_MS = 24 * 60 * 60 * 1000
const cycleTick = new Intl.DateTimeFormat('en-US', { month: 'short', day: 'numeric', timeZone: 'UTC' })
const cycleDay = new Intl.DateTimeFormat('en-US', { weekday: 'short', month: 'short', day: 'numeric', timeZone: 'UTC' })

/**
 * One column per payment cycle from `GET /analytics/over-time?grain=cycle`
 * (§16.10, D-469), oldest first. In is the invoices uploaded in the cycle, Out
 * the invoices exported in it. The answer ends with the cycle holding today, so
 * the last column is the current one, still partial, and reads "This Cycle";
 * the others read their Wednesday. The tooltip names the whole Wednesday to
 * Tuesday.
 */
export function paymentCycleColumns(
  periods: readonly { period_start: string; invoice_count: number; export_count: number }[],
): InOutDay[] {
  return periods.map((period, index) => {
    const start = new Date(`${period.period_start}T00:00:00Z`)
    const range = `${cycleDay.format(start)} – ${cycleDay.format(new Date(start.getTime() + 6 * DAY_MS))}`
    const current = index === periods.length - 1
    return {
      date: period.period_start,
      label: current ? 'This Cycle' : cycleTick.format(start),
      title: current ? `This payment cycle, ${range}` : range,
      in: period.invoice_count,
      out: period.export_count,
    }
  })
}
